import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import BigDrawer from "@/components/Drawer/BigDrawer";
import AddUniversity from "@/components/Lead/University/AddUniversity";
import AddContactStage from "@/components/CRM/Setup/ContactSetup/ContactStage/AddContactStage";
import { loadAllAgent } from "@/redux/rtk/features/CRM/agent/agentSlice";
import { loadAllContactSource } from "@/redux/rtk/features/CRM/contactSource/contactSourceSlice";
import { loadAllContactStage } from "@/redux/rtk/features/CRM/contactStage/contactStageSlice";
import { loadAllIndustry } from "@/redux/rtk/features/CRM/industry/industrySlice";
import { loadAllStaff } from "@/redux/rtk/features/hrm/user/userSlice";
import { useGetMajorsQuery } from "@/redux/rtk/features/major/majorApi";
import { useGetUniversitiesQuery } from "@/redux/rtk/features/university/universityApi";
import {
  useConvertLeadMutation,
  useGetLeadQuery,
  useUpdateLeadMutation,
} from "@/redux/rtk/features/leads/leadsApi";
import { useGetApplicationsQuery } from "@/redux/rtk/features/application/applicationApi";
import { Button, DatePicker, Form, Input, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NATIONALITIES } from "@/utils/nationalities";
import usePermissions from "@/utils/usePermissions";

// Helper function to get ID from value (handles both ID and object)
const getId = (value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "object" && value !== null && value.id !== undefined) {
    return value.id;
  }
  if (typeof value === "number") return value;
  if (typeof value === "string" && value !== "") {
    const num = parseInt(value);
    return isNaN(num) ? undefined : num;
  }
  return undefined;
};

const normalizeGender = (value) => {
  if (!value) return "";
  const normalized = String(value).toLowerCase();
  if (normalized === "male") return "Male";
  if (normalized === "female") return "Female";
  return value;
};

export default function ConvertLead({ onClose, createAs }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const userId = localStorage.getItem("id");
  const userName = localStorage.getItem("user");

  const { data: leads, isLoading: leadsLoading } = useGetLeadQuery(id);
  const { permissions } = usePermissions();
  const canReadAllUser = permissions?.includes("readAll-user");
  // selector
  const { list: companyList, loading: companyLoading } = useSelector(
    (state) => state.agent
  );

  const { list: staffList, loading: staffLoading } = useSelector(
    (state) => state.users
  );
  const { loading: contactLoading } = useSelector((state) => state.contact);
  const { list: contactStageList } = useSelector((state) => state.contactStage);

  const { data: majors } = useGetMajorsQuery();
  const { data: universitiesData } = useGetUniversitiesQuery();
  const { data: applications } = useGetApplicationsQuery(id ? { lead_id: id } : {}, { skip: !id });
  const universities = Array.isArray(universitiesData) ? universitiesData : (universitiesData?.universities ?? universitiesData?.getAllUniversity ?? []);

  const [form] = Form.useForm();
  const watchedUniversityIds = Form.useWatch("universityIds", form) || [];
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadAllContactSource());
    dispatch(loadAllAgent());
    dispatch(loadAllContactStage());
    dispatch(loadAllIndustry());
    dispatch(loadAllStaff({ query: "all" }));
  }, [dispatch]);

  useEffect(() => {
    // Set initial values when `leads` data is available
    if (leads) {
      const appList = applications?.getAllApplication || [];
      const uniIds = appList.map(app => app.universityId).filter(Boolean);
      const firstMajorId = appList[0]?.majorId;

      form.setFieldsValue({
        email: leads.email || "",
        firstName: leads.firstName || leads.name || "",
        lastName: leads.lastName || "",
        dateOfBirth: leads.dateOfBirth ? dayjs(leads.dateOfBirth) : undefined,
        contactOwnerId: getId(leads?.leadOwnerId) || getId(leads?.leadOwner?.id) || parseInt(userId), // Set current lead owner or current user
        gender: normalizeGender(leads.gender),
        companyId: leads.companyId || undefined,
        jobTitle: leads.jobTitle || "",
        phone: leads.phone || "",
        mobile: leads.mobile || "",
        department: leads.department || "",
        twitter: leads.twitter || "",
        linkedin: leads.linkedin || "",
        presentAddress: leads.presentAddress || "",
        presentCity: leads.presentCity || "",
        presentZipCode: leads.presentZipCode || "",
        presentState: leads.presentState || "",
        presentCountry: leads.presentCountry || "",
        permanentAddress: leads.permanentAddress || "",
        permanentCity: leads.permanentCity || "",
        permanentZipCode: leads.permanentZipCode || "",
        permanentState: leads.permanentState || "",
        permanentCountry: leads.permanentCountry || "",
        industryId: leads.industryId || undefined,
        contactSourceId: leads.contactSourceId || undefined,
        contactStageId: leads.contactStageId || undefined,
        description: leads.description || "",
        // prefill new fields if present
        dealName: leads.dealName || (leads.firstName ? `${leads.firstName} ${leads.lastName || ''} - ${t("lead.deal")}` : leads.name ? `${leads.name} - ${t("lead.deal")}` : ""),
        closingDate: leads.closingDate ? dayjs(leads.closingDate) : dayjs().add(1, 'month'),
        passportNo: leads.passportNo || "",
        nationality: leads.nationality || "",
        majorId: leads.majorId || firstMajorId || undefined,
        universityIds: leads.universityIds?.length > 0 ? leads.universityIds : (uniIds.length > 0 ? uniIds : []),
      });
    }
  }, [leads, form, userId, applications, t]);

  const [pageConfig, setPageConfig] = useState({
    query: "changeContactStatus",
    isConverted: true,
  });

  const [convertLead, { isLoading }] = useConvertLeadMutation();

  const onFinish = async (values) => {
    const formData = {
      ...values,
      dealName: `${values.firstName} ${values.lastName || ""}`.trim(),
      dateOfBirth: values.dateOfBirth
        ? dayjs(values.dateOfBirth).format("YYYY-MM-DD")
        : undefined,
      closingDate: values.closingDate
        ? dayjs(values.closingDate).format("YYYY-MM-DD")
        : undefined,
      contactOwnerId: parseInt(values.contactOwnerId),
      companyId: values.companyId ? parseInt(values.companyId) : undefined,
      industryId: values.industryId ? parseInt(values.industryId) : undefined,
      contactSourceId: values.contactSourceId
        ? parseInt(values.contactSourceId)
        : undefined,
      contactStageId: values.contactStageId
        ? parseInt(values.contactStageId)
        : undefined,
      majorId: values.majorId ? parseInt(values.majorId) : undefined,
      universityIds: values.universityIds || [],
      gender: values.gender || undefined,
    };

    const resp = await convertLead({ data: formData, arg: pageConfig, id });
    if (resp.data) {
      form.resetFields();
      onClose();
      // Navigate back to leads list after successful conversion
      navigate("/admin/lead");
    }
  };

  const [showMoreDetails, setShowMoreDetails] = useState(false);

  return (
    <UserPrivateComponent permission="create-contact">
      <Form
        className="px-5"
        onFinish={onFinish}
        colon={false}
        layout="vertical"
        form={form}
        initialValues={
          createAs
            ? {
              [createAs.name]: createAs.value,
            }
            : {}
        }>
        {/* Hidden fields placed outside the grid to not affect layout */}
        <Form.Item hidden name="contactStageId" />
        <Form.Item hidden name="companyId" />
        <Form.Item hidden name="jobTitle" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
          <Form.Item
            label={t("lead.dealName")}
            name="dealName"
            hidden
            rules={[{ required: true, message: t("lead.dealNameRequired") }]}>
            <Input placeholder={t("lead.dealName")} />
          </Form.Item>

          <Form.Item
            label={t("lead.closingDate")}
            name="closingDate"
            rules={[{ required: true, message: t("lead.closingDateRequired") }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item
            label={t("contact.owner")}
            name="contactOwnerId"
            rules={[{ required: true, message: t("contact.selectOwner") }]}>
            <Select
              key={staffLoading ? "loading" : "ready"}
              style={{ width: "100%" }}
              loading={staffLoading}
              allowClear
              showSearch
              placeholder={t("contact.selectOwner")}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={(() => {
                const options = (canReadAllUser && staffList) ? [...staffList] : [];
                const cId = getId(form.getFieldValue("contactOwnerId")) || getId(leads?.leadOwnerId) || getId(leads?.leadOwner?.id);
                
                let cName = leads?.ownerFullName || (leads?.leadOwner && (leads.leadOwner.username || `${leads.leadOwner.firstName || ''} ${leads.leadOwner.lastName || ''}`.trim()));
                
                // If the owner is the current user and we don't have a name from the lead, use the name from localStorage
                if (getId(cId) === getId(userId) && !cName) {
                  cName = userName;
                }

                if (cId && !options.some((item) => getId(item.id) === cId)) {
                  options.push({
                    id: cId,
                    username: cName || `User ${cId}`,
                    firstName: "",
                    lastName: "",
                  });
                }
                return options.map((item) => ({
                  value: getId(item.id),
                  label:
                    item?.username ||
                    (item.firstName || item.lastName
                      ? `${item.firstName || ""} ${item.lastName || ""}`.trim()
                      : null) ||
                    `User ${item.id}`,
                }));
              })()}
            />
          </Form.Item>

          <Form.Item
            label={t("lead.passportNo")}
            name="passportNo"
            rules={[{ required: true, message: t("lead.passportNoRequired") }]}>
            <Input placeholder={t("lead.passportNo")} />
          </Form.Item>

          <Form.Item
            label={t("lead.nationality")}
            name="nationality"
            rules={[{ required: true, message: t("lead.nationalityRequired") }]}>
            <Select
              showSearch
              allowClear
              placeholder={t("lead.nationality")}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={NATIONALITIES}
            />
          </Form.Item>

          <Form.Item label={t("contact.birthday")} name="dateOfBirth">
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" placeholder={t("contact.selectBirthday")} />
          </Form.Item>

          <Form.Item
            label={t("application.gender")}
            name="gender"
            rules={[{ required: true, message: t("application.selectGender") }]}
          >
            <Select
              placeholder={t("application.selectGender")}
              options={[
                { label: t("application.male"), value: "Male" },
                { label: t("application.female"), value: "Female" },
              ]}
            />
          </Form.Item>

          <Form.Item
            label={t("deal.priority")}
            name="priority"
            initialValue="Direct"
            rules={[{ required: true, message: t("deal.selectPriority") }]}>
            <Select placeholder={t("deal.selectPriority")}>
              <Select.Option value="Direct">{t("deal.direct")}</Select.Option>
              <Select.Option value="Transfer">{t("deal.transfer")}</Select.Option>
            </Select>
          </Form.Item>

          <div className="md:col-span-2 flex flex-col">
            <Form.Item
              label={t("university.universities")}
              name="universityIds"
              rules={[{ required: true, message: t("lead.universityRequired") }]}
              style={{ marginBottom: watchedUniversityIds?.length > 0 ? '8px' : '24px' }}>
              <Select
                key={leadsLoading ? "loading" : "ready"}
                mode="multiple"
                style={{ width: "100%" }}
                allowClear
                showSearch
                placeholder={t("lead.selectUniversities")}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                tagRender={() => <span style={{ display: 'none' }} />}
                options={(() => {
                  const currentList = Array.isArray(universities) ? [...universities] : [];
                  // If there are applications, ensure those universities are in the list
                  const appList = applications?.getAllApplication || [];
                  appList.forEach(app => {
                    const uId = getId(app.universityId);
                    if (uId && !currentList.some(u => getId(u.id) === uId)) {
                      currentList.push({
                        id: uId,
                        universityName: app.university?.universityName || app.university?.name || `University ${uId}`
                      });
                    }
                  });
                  return currentList.map((u) => ({
                    label: u.universityName || u.name,
                    value: getId(u.id),
                  }));
                })()}
              />
            </Form.Item>
            {watchedUniversityIds?.length > 0 && (
              <div className="flex flex-col gap-2 mb-4">
                {watchedUniversityIds.map(uid => {
                  const uni = universities?.find(u => getId(u.id) === uid);
                  if (!uni) return null;
                  return (
                    <div key={uid} className="flex justify-between items-center bg-blue-50/50 border border-blue-100 px-3 py-2 rounded-md">
                      <span className="text-sm break-words whitespace-normal leading-tight text-gray-700" title={uni.universityName || uni.name}>
                        {uni.universityName || uni.name}
                      </span>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-red-500 cursor-pointer p-1 ml-2 shrink-0 transition-colors"
                        onClick={(e) => {
                          e.preventDefault();
                          const newIds = watchedUniversityIds.filter(id => id !== uid);
                          form.setFieldsValue({ universityIds: newIds });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Form.Item
            label={t("lead.major")}
            name="majorId"
            rules={[{ required: true, message: t("lead.interestRequired") }]}>
            <Select
              key={leadsLoading ? "loading" : "ready"}
              style={{ width: "100%" }}
              loading={false}
              allowClear
              showSearch
              placeholder={t("lead.selectMajor")}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={(() => {
                const currentList = Array.isArray(majors) ? [...majors] : [];
                const cId = getId(form.getFieldValue("majorId")) || getId(leads?.majorId) || getId(leads?.majorRel?.id);
                const cName = leads?.majorName || leads?.majorRel?.name;

                if (cId && !currentList.some(m => getId(m.id) === cId)) {
                  currentList.push({ id: cId, name: cName || `Major ${cId}` });
                }
                return currentList.map((item) => ({
                  value: getId(item.id),
                  label: item.name,
                }));
              })()}
            />
          </Form.Item>

          <Form.Item
            label={t("lead.firstName")}
            name="firstName"
            rules={[{ required: true, message: t("lead.firstNameRequired") }]}>
            <Input placeholder={t("lead.firstName")} />
          </Form.Item>

          <Form.Item label={t("lead.lastName")} name="lastName">
            <Input placeholder={t("lead.lastName")} />
          </Form.Item>

          <Form.Item
            label={t("lead.mobile")}
            name="mobile"
            normalize={(value) => value.replace(/[^0-9+]/g, "")}
            rules={[
              { required: true, message: t("lead.mobileRequired") },
              { pattern: /^\+?[0-9]+$/, message: t("lead.invalidPhoneNumber") }
            ]}>
            <Input placeholder={t("lead.mobile") || "+1 (273) 642-1785"} />
          </Form.Item>

          <Form.Item
            label={t("lead.phoneNumber")}
            name="phone"
            normalize={(value) => value.replace(/[^0-9+]/g, "")}
            rules={[
              { pattern: /^\+?[0-9]+$/, message: t("lead.invalidPhoneNumber") }
            ]}>
            <Input placeholder={t("lead.phoneNumber") || "+1 (273) 642-1785"} />
          </Form.Item>

          <Form.Item
            label={t("common.email")}
            name="email"
            rules={[
              {
                type: "email",
                message: t("common.pleaseEnterValidEmail"),
              },
              {
                pattern: /^[^\u0600-\u06FF]*$/,
                message: t("common.arabicCharactersNotAllowed"),
              },
            ]}
          >
            <Input placeholder={t("common.email")} />
          </Form.Item>

          {/* More Details Fields integrated into the same grid */}
          {showMoreDetails && (
            <>
              <Form.Item label={t("contact.department")} name="department">
                <Input placeholder={t("contact.department")} />
              </Form.Item>
              <Form.Item label={t("contact.presentAddress")} name="presentAddress">
                <Input placeholder={t("contact.enterPresentAddress")} />
              </Form.Item>
              <Form.Item label={t("contact.city")} name="presentCity">
                <Input placeholder={t("contact.city")} />
              </Form.Item>
              <Form.Item label={t("contact.country")} name="presentCountry">
                <Input placeholder={t("contact.country")} />
              </Form.Item>
            </>
          )}
          <Form.Item
            label={t("lead.description")}
            name="description"
            className="md:col-span-2">
            <Input.TextArea rows={4} placeholder={t("lead.enterDescription")} />
          </Form.Item>
        </div>

        {/* More Details Toggle */}
        <Button
          type="link"
          loading={isLoading}
          onClick={() => setShowMoreDetails(!showMoreDetails)}
          className="text-white bg-[#40a9ff] mt-4">
          {showMoreDetails ? (
            <span className="flex items-center gap-2">
              {t("lead.showLessDetails")} <IoIosArrowUp />
            </span>
          ) : (
            <span className="flex items-center gap-2">
              {t("lead.showMoreDetails")} <IoIosArrowDown />
            </span>
          )}
        </Button>

        {/* Submit and Cancel Buttons */}
        <Form.Item>
          <div className="flex items-center mt-3 justify-end gap-2">
            <Button
              size={"large"}
              htmlType="submit"
              type="primary"
              loading={contactLoading}>
              {t("lead.convert")}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </UserPrivateComponent>
  );
}
